import { createSlice, PayloadAction, current } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

import { RootState } from '~/redux/store'
import { BookingLanguage, LevelOfEducation, Location, Subject, TutorPackage } from '~/types/booking'
import { productApi } from '~/redux/apis/product-api'
import {
  BookForSelfFormErrors,
  TutorForOtherBookingFields,
  TutorForSelfBookingFields,
  VideoChatBookingFields,
} from '~/types/cart'
import { logout } from '~/auth/slices/auth.slice'

interface CartState {
  tutorForOther: TutorForOtherBookingFields
  tutorForSelf: TutorForSelfBookingFields
  videoChat: VideoChatBookingFields
  bookingForSelfFormErrors: BookForSelfFormErrors
}

export type CartKey = keyof CartState

const initialBookingCommonState = {
  levelsOfEducation: [LevelOfEducation.GRADE_11_TO_13],
  product: null,
  subjects: [Subject.MATH],
}

export const initialTutorBookingCommonState = {
  ...initialBookingCommonState,
  includeTravelExpense: false,
  location: Location.ONLINE,
}

const initialTutorBookingForOtherState = {
  ...initialTutorBookingCommonState,
  email: '',
  emailVerify: '',
  firstName: '',
  lastName: '',
  uuid: '',
}

const initialVideoChatBookingState = {
  ...initialBookingCommonState,
  language: undefined,
  dateAsTimestamp: null,
  needHelpWith: '',
  scheduleAt: null,
  subjects: [Subject.MATH],
}

function generateTutorForOtherItem(): TutorForOtherBookingFields[number] {
  return {
    ...initialTutorBookingForOtherState,
    uuid: uuidv4(),
  }
}

const initialBookForSelfFormErrors: BookForSelfFormErrors = {
  location: undefined,
  product: undefined,
  levelsOfEducation: undefined,
  language: undefined,
  subjects: undefined,
}

const initialState: CartState = {
  tutorForOther: [generateTutorForOtherItem()],
  tutorForSelf: initialTutorBookingCommonState,
  videoChat: initialVideoChatBookingState,
  bookingForSelfFormErrors: initialBookForSelfFormErrors,
}

const slice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addTutorForOtherItem: (state) => ({
      ...state,
      tutorForOther: [...state.tutorForOther, generateTutorForOtherItem()],
    }),

    clearCart: (state, action: PayloadAction<CartKey>) => ({
      ...state,
      [action.payload]: initialState[action.payload],
    }),

    removeTutorForOtherItem: (state, action: PayloadAction<string>) => ({
      ...state,
      tutorForOther:
        state.tutorForOther.length > 1
          ? state.tutorForOther.filter((elem) => elem.uuid !== action.payload)
          : state.tutorForOther,
    }),

    setTutorForOtherCart: (state, action: PayloadAction<Partial<CartState['tutorForOther'][number]>>) => ({
      ...state,
      tutorForOther: state.tutorForOther.map((elem) => {
        return action.payload.uuid === elem.uuid ? { ...elem, ...action.payload } : { ...elem }
      }),
    }),

    setTutorForSelfCart: (state, action: PayloadAction<Partial<CartState['tutorForSelf']>>) => ({
      ...state,
      tutorForSelf: {
        ...state.tutorForSelf,
        ...action.payload,
      },
    }),

    setVideoChatCart: (state, action: PayloadAction<Partial<CartState['videoChat']>>) => ({
      ...state,
      videoChat: {
        ...state.videoChat,
        ...action.payload,
      },
    }),

    setBookingForSelfFormError: (state, action: PayloadAction<{ fieldName: string; message: string | undefined }>) => ({
      ...state,
      bookingForSelfFormErrors: {
        ...state.bookingForSelfFormErrors,
        [action.payload.fieldName]: action.payload.message,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)

    builder.addMatcher(productApi.endpoints.getProducts.matchFulfilled, (state, { payload }) => {
      const currentState = current(state)
      const correctTutoringProduct = payload.tutoringProducts.find(
        (product) => product.type === (currentState.tutorForSelf.product?.type ?? TutorPackage.HOURS_4),
      )

      state.tutorForSelf.product = correctTutoringProduct ?? null

      const userLanguage = currentState.tutorForSelf.language
      const hasNorwegianBrowserLanguage =
        typeof navigator !== 'undefined' &&
        navigator.languages?.some((language) => {
          const languageCode = language?.slice(0, 2)
          return ['no', 'nb', 'nn'].includes(languageCode)
        })
      state.tutorForSelf.language =
        userLanguage ?? (hasNorwegianBrowserLanguage ? BookingLanguage.NORWEGIAN : BookingLanguage.ENGLISH)
    })
  },
})

export const selectTutorForOtherCart = (state: RootState): CartState['tutorForOther'] => {
  return state.cart.tutorForOther
}

export const selectTutorForSelfCart = (state: RootState): CartState['tutorForSelf'] => {
  return state.cart.tutorForSelf
}

export const selectVideoChatCart = (state: RootState): CartState['videoChat'] => {
  return state.cart.videoChat
}

export const selectVideoChatCartDate = (state: RootState): Date | null => {
  const timestamp = state.cart.videoChat.dateAsTimestamp
  return timestamp ? new Date(timestamp) : null
}

export const selectVideoChatCartSlot = (state: RootState): Date | null => {
  const isostring = state.cart.videoChat.scheduleAt
  return isostring ? new Date(isostring) : null
}

export const selectBookingForSelfFormErrors = (state: RootState): BookForSelfFormErrors => {
  return state.cart.bookingForSelfFormErrors
}
// ACTIONS
//

export const updateCartFromLatestBooking =
  ({ preference, product }) =>
  (dispatch, getState) => {
    const tutorForSelfCart = getState().cart.tutorForSelf
    dispatch(
      setTutorForSelfCartActionCreator({
        language: tutorForSelfCart.language ?? preference.languagePrimary ?? undefined,
        levelsOfEducation:
          tutorForSelfCart.levelsOfEducation.length === 0
            ? preference.subjects.levelsOfEducation
            : tutorForSelfCart.levelsOfEducation,
        product,
        subjects:
          tutorForSelfCart.subjects.length === 0
            ? preference.subjects.subjects.map((subject) => subject.name)
            : tutorForSelfCart.subjects,
        // includeTravelExpense: tutorForSelfCart.includeTravelExpense ?? preference.includeTravelExpense,
        location: tutorForSelfCart.location ?? preference.city,
      }),
    )
  }

export const updateCartFromPreference = (preference) => (dispatch, getState) => {
  const tutorForSelfCart = getState().cart.tutorForSelf
  dispatch(
    setTutorForSelfCartActionCreator({
      language: tutorForSelfCart.language ?? preference.languagePrimary,
      levelsOfEducation:
        tutorForSelfCart.levelsOfEducation.length === 0
          ? preference.levelsOfEducation
          : tutorForSelfCart.levelsOfEducation,
      subjects: tutorForSelfCart.subjects.length === 0 ? preference.subjects : tutorForSelfCart.subjects,
      // includeTravelExpense: tutorForSelfCart.includeTravelExpense ?? latestBooking.includeTravelExpense,
      location: tutorForSelfCart.location ?? preference.city,
    }),
  )
}

export const {
  addTutorForOtherItem: addTutorForOtherItemActionCreator,
  clearCart: clearCartActionCreator,
  removeTutorForOtherItem: removeTutorForOtherItemActionCreator,
  setTutorForOtherCart: setTutorForOtherCartActionCreator,
  setTutorForSelfCart: setTutorForSelfCartActionCreator,
  setVideoChatCart: setVideoChatCartActionCreator,
  setBookingForSelfFormError: setBookingForSelfFormErrorActionCreator,
} = slice.actions

export default slice.reducer
